import type { CaseFieldOption } from "../model/caseReview";

type JsonSchema = Record<string, unknown>;

/** A plain string enum, not a codelist-backed coded field — the two read as options differently. */
export function isEnumProperty(schemaProperty: JsonSchema): boolean {
  return Array.isArray(schemaProperty.enum) && typeof schemaProperty["x-codelist"] !== "string";
}

/**
 * The options an enum-backed field offers, from the schema's own declared members.
 *
 * <p>The compiler emits every enum with a trailing `null` (the field is nullable) — not a
 * selectable option, dropped here along with anything else that isn't a string. If the stored
 * value is a non-empty string the declared members don't contain, it is prepended as an inactive
 * option (D93a): the shared `CaseFieldSelect` already keeps an inactive option on screen while it
 * is the stored value and drops it the moment another is picked, so this only has to supply it
 * once, the same way a retired codelist row is handled.
 */
export function enumOptionsFor(schemaProperty: JsonSchema, storedValue: unknown): CaseFieldOption[] {
  const members = (Array.isArray(schemaProperty.enum) ? schemaProperty.enum : [])
    .filter((value): value is string => typeof value === "string");
  const options: CaseFieldOption[] = members.map((value) => ({ id: value, label: value, active: true }));
  if (typeof storedValue === "string" && storedValue !== "" && !members.includes(storedValue)) {
    options.unshift({ id: storedValue, label: storedValue, active: false });
  }
  return options;
}
